import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography } from 'seagull';
import { AnchorLogo } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import ContactSupportButton from '~/components/ContactSupportButton';
import { redirect, routes } from '~/constants';
import { useEventReporter } from '~/event-reporter/EventReporter';
import { useLocationWithState } from '~/hooks/useLocationWithState';
import { useOnMount } from '~/hooks/useOnMount';
import { useLocaleKeys } from '~/i18n';
import { useIoContext } from '~/providers/IoContext';
import { selectors } from '~/store/state-selectors';
import { useStore, useStoreActions } from '~/store/store-context';
import { SetLoginFlow } from '../unAuthorizedPages/LoginScreen/LoginScreen.types';
import styles from './NoAccessPage.module.scss';
export const GetNoAccessPageTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            NoAccessPageTID: {
                Root: 'notification-no-access-page',
                Header: 'notification-no-access-header',
                Description: 'notification-no-access-description',
                BackToDashboardButton: 'notification-no-access-back-to-dashboard-button',
                LogoutButton: 'notification-no-access-logout-button'
            }
        }
    });
export const NoAccessPage = ()=>{
    const { t: noAccessPageTexts } = useLocaleKeys('pages.noAccessPage');
    const { setAuthToken } = useStoreActions();
    const { native } = useIoContext();
    const navigate = useNavigate();
    const location = useLocationWithState();
    const locationState = location.state;
    const existingEmail = useStore(selectors.user.email);
    const recipientEmail = locationState === null || locationState === void 0 ? void 0 : locationState.recipientEmail;
    const returnTo = locationState === null || locationState === void 0 ? void 0 : locationState.returnTo;
    const isVendor = useStore(selectors.user.isVendor);
    const { logBI } = useStoreActions();
    const { sendEventToGtm } = useEventReporter();
    const hasAuthToken = useStore((state)=>state.authToken != null);
    const { NoAccessPageTID } = useMemo(()=>GetNoAccessPageTID(), []);
    useOnMount(()=>{
        if (recipientEmail) {
            logBI(15101);
        } else {
            logBI(15100);
        }
    });
    const descriptionText = recipientEmail && hasAuthToken ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            "This link is meant for ",
            /*#__PURE__*/ _jsx("b", {
                children: recipientEmail
            }),
            ", but you're logged in as ",
            /*#__PURE__*/ _jsx("b", {
                children: existingEmail
            }),
            " which does not have the necessary permissions to access this asset.",
            /*#__PURE__*/ _jsx("br", {}),
            /*#__PURE__*/ _jsx("br", {}),
            "To switch users, log out and sign in with the relevant email address. Make sure to save your work first."
        ]
    }) : /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            "If you believe you should have access or require additional permissions, please contact your account owner or ",
            /*#__PURE__*/ _jsx(ContactSupportButton, {
                hideContactText: true,
                underline: "none",
                variant: "h5"
            }),
            "."
        ]
    });
    async function logOutUser() {
        sendEventToGtm('logout', {});
        logBI(15103);
        await native.setJustLoggedOut();
        await setAuthToken(null);
        const setLoginFlow = isVendor ? SetLoginFlow.FullLogin : SetLoginFlow.OtpLogin;
        navigate(redirect.getPathAndSearch({
            returnTo
        }), {
            replace: true,
            state: {
                setLoginFlow
            }
        });
    }
    async function backToDashboard() {
        logBI(15102);
        navigate(routes.home, {
            replace: true
        });
    }
    return /*#__PURE__*/ _jsxs(Box, {
        testId: NoAccessPageTID.Root(),
        className: styles.noAccessPageContainer,
        children: [
            /*#__PURE__*/ _jsx(AnchorLogo, {
                theme: "dark",
                height: 44
            }),
            /*#__PURE__*/ _jsx(Typography, {
                variant: "h3",
                weight: "normal",
                testId: NoAccessPageTID.Header(),
                children: noAccessPageTexts.noPermissions()
            }),
            /*#__PURE__*/ _jsx(Typography, {
                variant: "h5",
                testId: NoAccessPageTID.Description(),
                className: styles.description,
                children: descriptionText
            }),
            hasAuthToken && /*#__PURE__*/ _jsxs(Box, {
                className: styles.buttonContainer,
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'no_access_back_to_dashboard'
                        },
                        skin: recipientEmail ? 'primaryOutline' : 'primary',
                        onClick: backToDashboard,
                        testId: NoAccessPageTID.BackToDashboardButton(),
                        children: noAccessPageTexts.backToDashboard()
                    }),
                    recipientEmail && /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'no_access_log_out'
                        },
                        skin: "primary",
                        onClick: logOutUser,
                        testId: NoAccessPageTID.LogoutButton(),
                        children: noAccessPageTexts.logOut()
                    })
                ]
            })
        ]
    });
};
